/**
 * Turning a rejected API response into something a human can act on.
 *
 * WHY THIS EXISTS. The controllers here resolve rather than throw — every one of
 * them returns `{ json, status }`, so a 400 or a 404 arrives looking exactly like
 * a success and the surrounding `try/catch` never fires. Paired with the house
 * idiom `if (response.status < 250) { ...ok... }` and no `else`, a server
 * rejection becomes a button that does nothing at all: no message, no spinner
 * left running, nothing in the console. That is how a real FTC spent a session
 * clicking Save on a patient who had no insurance policy, with the server
 * answering `{"insurance_policy": ["This field is required."]}` every time.
 *
 * The established one-liner elsewhere in this codebase —
 *   (json && (json.error || json.detail)) || fallback
 * — reads DRF's two TOP-LEVEL error shapes but silently misses the third and
 * most common one: per-FIELD validation errors, which are what a required-field
 * failure actually looks like. Those are the ones worth showing, so this reads
 * all three.
 */

/** DRF puts field errors under the field name, usually as a list of strings. */
function fieldErrors(json) {
  const out = [];
  Object.keys(json).forEach(function (key) {
    const value = json[key];
    const text = Array.isArray(value)
      ? value.filter((v) => typeof v === "string").join(" ")
      : typeof value === "string"
        ? value
        : null;
    if (!text) return;
    // "non_field_errors" is DRF's bucket for object-level failures and reads
    // badly as a label; the message alone is the whole point there.
    out.push(key === "non_field_errors" ? text : `${key.replace(/_/g, " ")}: ${text}`);
  });
  return out;
}

/**
 * A one-line description of why `response` was rejected.
 *
 * `fallback` is used when the body says nothing useful — never returns empty,
 * because a message box with no message is the bug this module exists to fix.
 */
export function describeApiError(response, fallback) {
  const backstop = fallback || "Something went wrong. Please try again.";
  if (!response) return backstop;

  const json = response.json;
  if (!json || typeof json !== "object") {
    return response.status ? `${backstop} (${response.status})` : backstop;
  }

  // The two shapes the rest of the codebase already looks for.
  if (typeof json.error === "string" && json.error) return json.error;
  if (typeof json.detail === "string" && json.detail) return json.detail;

  // …and the per-field shape it did not.
  const fields = fieldErrors(json);
  if (fields.length) return fields.join(" · ");

  return response.status ? `${backstop} (${response.status})` : backstop;
}

export default describeApiError;
